import React, { useState } from 'react';
import { Search, MapPin } from 'lucide-react';

/**
 * SearchBar - Main search component for the home page
 * Allows users to search by make, model, body style, and location
 */
const SearchBar = ({ onSearch, onLocationRequest }) => {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    bodyStyle: '',
    location: '',
    availableToday: false,
  });

  const bodyStyles = ['SUV', 'Sedan', 'Truck', 'EV'];
  const popularMakes = ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Tesla', 'Nissan'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(formData);
  };

  const handleNearMeClick = () => {
    onLocationRequest();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-6 shadow-lg">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {/* Make */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Make
          </label>
          <select
            name="make"
            value={formData.make}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent"
          >
            <option value="">Any Make</option>
            {popularMakes.map(make => (
              <option key={make} value={make}>{make}</option>
            ))}
          </select>
        </div>

        {/* Model */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Model
          </label>
          <input
            type="text"
            name="model"
            value={formData.model}
            onChange={handleChange}
            placeholder="Enter model"
            className="w-full px-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary"
          />
        </div>

        {/* Body Style */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Body Style
          </label>
          <select
            name="bodyStyle"
            value={formData.bodyStyle}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent"
          >
            <option value="">Any Style</option>
            {bodyStyles.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Location
          </label>
          <div className="relative">
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="City, State, or ZIP"
              className="w-full px-4 py-3 pr-10 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary"
            />
            <button
              type="button"
              onClick={handleNearMeClick}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary rounded-lg transition-colors"
              title="Use my location"
            >
              <MapPin className="w-5 h-5 text-app-accent" />
            </button>
          </div>
        </div>
      </div>

      {/* Available Today Toggle */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="checkbox"
          id="availableToday"
          name="availableToday"
          checked={formData.availableToday}
          onChange={handleChange}
          className="w-4 h-4 text-app-accent border-app-border-light dark:border-app-border rounded focus:ring-app-accent focus:ring-2"
        />
        <label htmlFor="availableToday" className="text-sm font-medium text-app-text-light dark:text-app-text cursor-pointer">
          Available Today
        </label>
      </div>

      {/* Search Button */}
      <button
        type="submit"
        className="w-full bg-app-accent hover:bg-app-accent/90 text-white py-4 rounded-xl font-bold text-lg transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-2"
      >
        <Search className="w-5 h-5" />
        Search Test Drives
      </button>
    </form>
  );
};

export default SearchBar;
